/**
 * LayoutSaScope — SaOrgScopeSelector + SaScopeBanner estratti da Layout.jsx (R38).
 *
 * Permettono al SuperAdmin di filtrare la vista come admin di una specifica
 * azienda (Iter101: SA può scrivere mentre è in scope, non è impersonation read-only).
 *
 * Esportati separatamente per ridurre Layout.jsx da 769 → ~630 righe.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Building2, Eye, ShieldCheck, LogOut } from "lucide-react";

export function SaOrgScopeSelector({ currentScope }) {
  const { refresh, patchUser } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/orgs").then(({ data }) => setOrgs(data || [])).catch(() => {});
  }, []);

  const apply = async (orgId) => {
    setBusy(true);
    const found = orgs.find((o) => o.id === orgId);
    patchUser({
      sa_view_org: orgId || null,
      org_name: orgId ? (found?.name || null) : null,
      enabled_sections: orgId ? undefined : null,
    });
    try {
      await api.post("/sa/scope", { org_id: orgId || null });
      refresh();
      setOpen(false);
      toast.success(orgId ? `Vista filtrata su ${found?.name || "azienda"}` : "Vista globale ripristinata", { duration: 1800 });
    } catch (e) {
      patchUser({ sa_view_org: currentScope || null });
      toast.error(e.response?.data?.detail || "Errore impostazione scope");
    } finally {
      setBusy(false);
    }
  };

  const current = orgs.find((o) => o.id === currentScope);
  const label = current ? current.name : "Vista globale";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="sa-org-scope-trigger"
          className={`flex items-center gap-2 text-xs sm:text-sm font-medium px-3 py-1.5 rounded-md border transition-colors ${currentScope ? "bg-amber-50 border-amber-300 text-amber-900" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
          disabled={busy}
        >
          <Building2 className="w-3.5 h-3.5" />
          <span className="max-w-[160px] truncate">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0" data-testid="sa-org-scope-panel">
        <div className="p-3 border-b">
          <p className="label-overline" style={{ fontSize: "0.6rem" }}>Vista amministratore</p>
          <p className="text-sm font-semibold">Filtra per azienda</p>
          <p className="text-xs text-slate-500 mt-1">Visualizza i dati HACCP come se fossi nell&apos;azienda selezionata.</p>
        </div>
        <div className="max-h-72 overflow-y-auto scroll-thin">
          <button
            onClick={() => apply(null)}
            data-testid="sa-org-scope-clear"
            className={`w-full text-left px-3 py-2 text-sm border-b hover:bg-slate-50 ${!currentScope ? "bg-slate-100 font-semibold" : ""}`}
          >
            <Eye className="w-3.5 h-3.5 inline mr-2" /> Vista globale (tutte)
          </button>
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => apply(o.id)}
              data-testid={`sa-org-scope-${o.id}`}
              className={`w-full text-left px-3 py-2 text-sm border-b hover:bg-slate-50 flex items-center justify-between ${currentScope === o.id ? "bg-amber-50 font-semibold" : ""}`}
            >
              <span className="truncate">{o.name}</span>
              <span className="text-[10px] text-slate-400 ml-2 shrink-0">{o.records_count} rec</span>
            </button>
          ))}
          {orgs.length === 0 && (
            <p className="px-3 py-4 text-xs text-slate-400 text-center">Nessuna azienda.</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function SaScopeBanner({ orgId }) {
  const { user, refresh, patchUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const orgName = user?.org_name || "";

  const exitScope = async () => {
    setBusy(true);
    patchUser({ sa_view_org: null, org_name: null, enabled_sections: null });
    try {
      await api.post("/sa/scope", { org_id: null });
      refresh();
      toast.success("Tornato alla vista direzione", { duration: 1500 });
    } catch (e) {
      patchUser({ sa_view_org: orgId });
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="px-4 sm:px-8 py-2 flex items-center gap-3 border-b text-sm"
      style={{ background: "#FEF3C7", borderColor: "#D4AF37", color: "#78350F" }}
      data-testid="sa-scope-banner"
    >
      <ShieldCheck className="w-4 h-4 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-semibold">Stai operando come admin di {orgName || "questa azienda"}</span>
        <span className="hidden sm:inline"> · le modifiche vengono registrate a tuo nome</span>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={exitScope}
        disabled={busy}
        className="bg-white hover:bg-amber-50 border-amber-300"
        data-testid="sa-scope-exit-btn"
      >
        <LogOut className="w-3.5 h-3.5 mr-1" /> {busy ? "Uscita…" : "Torna a direzione"}
      </Button>
    </div>
  );
}

/**
 * SaFloatingExitButton — pill flottante SEMPRE visibile (anche sopra modal/sheet/dialog).
 *
 * Gestisce DUE casi di "uscita rapida":
 *  • SA in scope su un'azienda (sa_view_org) → torna alla vista direzione
 *  • SA che sta "vedendo come" un cliente (impersonating) → torna SuperAdmin
 *
 * Nascosto se nessuno dei due casi è attivo.
 */
export function SaFloatingExitButton() {
  const { user, refresh, patchUser, stopImpersonation } = useAuth();
  const [busy, setBusy] = useState(false);

  const inImpersonation = !!user?.impersonating;
  const inScope = user?.role === "superadmin" && !!user?.sa_view_org && !inImpersonation;

  if (!inImpersonation && !inScope) return null;

  const exit = async () => {
    setBusy(true);
    if (inImpersonation) {
      try {
        const res = await stopImpersonation();
        if (res?.ok) {
          toast.success("Tornato al tuo account SA", { duration: 1500 });
        } else {
          toast.error(res?.error || "Errore uscita impersonazione");
        }
      } finally {
        setBusy(false);
      }
      return;
    }
    // Caso "scope" (sa_view_org)
    const prev = user.sa_view_org;
    patchUser({ sa_view_org: null, org_name: null, enabled_sections: null });
    try {
      await api.post("/sa/scope", { org_id: null });
      refresh();
      toast.success("Tornato alla vista direzione", { duration: 1500 });
    } catch (e) {
      patchUser({ sa_view_org: prev });
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const label = inImpersonation ? "Esci da Vedi come" : "Torna a SA";

  return (
    <button
      type="button"
      onClick={exit}
      disabled={busy}
      data-testid="sa-floating-exit-btn"
      aria-label={label}
      className="fixed top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg border transition-colors disabled:opacity-50"
      style={{
        zIndex: 99999,
        background: "#FEF3C7",
        borderColor: "#D4AF37",
        color: "#78350F",
      }}
    >
      <ShieldCheck className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">{busy ? "Uscita…" : label}</span>
      <LogOut className="w-3.5 h-3.5 sm:hidden" />
    </button>
  );
}
